import { ref } from 'vue';
// Simple in-memory cache store
const cacheStore = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes default
export function useDomainDataMethods(handler, cachable = false, cacheKey) {
    const state = ref({
        data: undefined,
        status: 'idle',
        error: undefined,
    });
    let lastOptions = undefined;
    /**
     * Get data from cache if available and valid
     */
    const getFromCache = (key) => {
        if (!cachable || !key)
            return undefined;
        const cached = cacheStore.get(key);
        if (!cached)
            return undefined;
        const isExpired = Date.now() - cached.timestamp > CACHE_DURATION;
        if (isExpired) {
            cacheStore.delete(key);
            return undefined;
        }
        return cached.data;
    };
    /**
     * Store data in cache
     */
    const setCache = (key, data) => {
        if (!cachable || !key)
            return;
        cacheStore.set(key, {
            data,
            timestamp: Date.now(),
        });
    };
    /**
     * Generate cache key from options
     */
    const generateCacheKey = (opts) => {
        if (!cachable)
            return '';
        const baseKey = cacheKey || 'default';
        if (!opts)
            return baseKey;
        try {
            return `${baseKey}:${JSON.stringify(opts)}`;
        }
        catch {
            return baseKey;
        }
    };
    /**
     * Execute the handler with cache and status management
     */
    const execute = async (opts) => {
        lastOptions = opts;
        // Check cache first
        const key = generateCacheKey(opts);
        const cachedData = getFromCache(key);
        if (cachedData !== undefined) {
            state.value = {
                data: cachedData,
                status: 'success',
                error: undefined,
            };
            return cachedData;
        }
        // Set loading state
        state.value = {
            ...state.value,
            status: 'loading',
            error: undefined,
        };
        try {
            const data = await handler(opts);
            // Set cache if enabled
            setCache(key, data);
            // Update state with success
            state.value = {
                data,
                status: 'success',
                error: undefined,
            };
            return data;
        }
        catch (error) {
            const errorObj = error instanceof Error ? error : new Error(String(error));
            // Update state with error
            state.value = {
                data: undefined,
                status: 'error',
                error: errorObj,
            };
            // eslint-disable-next-line no-console
            console.error('[useDomainDataMethods] Error executing handler:', errorObj);
            return undefined;
        }
    };
    /**
     * Refresh data using last options (bypass cache)
     */
    const refresh = async () => {
        if (cachable) {
            const key = generateCacheKey(lastOptions);
            cacheStore.delete(key);
        }
        await execute(lastOptions);
    };
    /**
     * Reset state to idle
     */
    const reset = () => {
        state.value = {
            data: undefined,
            status: 'idle',
            error: undefined,
        };
        lastOptions = undefined;
    };
    return {
        state,
        execute,
        refresh,
        reset,
    };
}
//# sourceMappingURL=useDomainDataMethod.js.map